import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import {
  ANCCAccreditation,
  CAPCEAccreditationV2,
  CourseMigratableContent,
  CourseV2Compliance,
  CourseV2Progress,
  CourseV2PublishReadiness,
  Instructor,
  LifecycleState,
  PlanningCourseV2,
  PlanningCourseV2Details,
  QuestionData,
  QuizV2,
  Section,
  Template,
  TextContent,
} from 'src/v3/core/types'
import {
  preArchiveCourse as preArchiveCourseAPI,
  reactivateCourse as reactivateCourseAPI,
  archiveCourse as archiveCourseAPI,
  restoreCourse as restoreCourseAPI,
} from './utils/course-lifecycle-api'
import {
  createPlanningCourseV2 as createPlanningCourseV2API,
  deletePlanningCourseV2 as deletePlanningCourseV2API,
  fetchCourseV2Compliances as fetchCourseV2CompliancesAPI,
  fetchPlanningCourseV2Details as fetchPlanningCourseV2DetailsAPI,
  fetchPlanningCourseV2Objectives as fetchPlanningCourseV2ObjectivesAPI,
  fetchPlanningCourseV2References as fetchPlanningCourseV2ReferencesAPI,
  fetchPlanningCoursesV2 as fetchPlanningCoursesV2API,
  fetchPlanningCoursesV2Progress as fetchPlanningCoursesV2ProgressAPI,
  updatePlanningCourseV2Core as updatePlanningCourseV2CoreAPI,
  updatePlanningCourseV2Objectives as updatePlanningCourseV2ObjectivesAPI,
  updatePlanningCourseV2References as updatePlanningCourseV2ReferencesAPI,
  updatePlanningCourseV2Instructors as updatePlanningCourseV2InstructorsAPI,
  fetchPlanningCourseV2AvailableInstructors as fetchPlanningCourseV2AvailableInstructorsAPI,
  createPlanningCourseV2Instructor as createPlanningCourseV2InstructorAPI,
  updatePlanningCourseV2Instructor as updatePlanningCourseV2InstructorAPI,
  updatePlanningCourseV2Syllabus as updatePlanningCourseV2SyllabusAPI,
  fetchCourseV2LegacyTextContent as fetchCourseV2LegacyTextContentAPI,
  fetchCourseV2ShowNotes as fetchCourseV2ShowNotesAPI,
  createCourseShowNotes as createCourseShowNotesAPI,
  updateCourseShowNotes as updateCourseShowNotesAPI,
  deleteCourseShowNotes as deleteCourseShowNotesAPI,
  fetchCourseV2Videos as fetchCourseV2VideosAPI,
  createCourseVideo as createCourseVideoAPI,
  updateCourseVideo as updateCourseVideoAPI,
  deleteCourseVideo as deleteCourseVideoAPI,
  updateCourseV2ContentSteps as updateCourseV2ContentStepsAPI,
  PipelineVideo,
  fetchCourseMigratableContent as fetchCourseMigratableContentAPI,
  migrateCourseV2Quiz as migrateCourseV2QuizAPI,
  migrateCourseV2Accreditation as migrateCourseV2AccreditationAPI,
  migrateCourseV2Video as migrateCourseV2VideoAPI,
  migrateCourseV2ShowNote as migrateCourseV2ShowNoteAPI,
  PipelineShowNotes,
  fetchCourseV2CAPCE as fetchCourseV2CAPCEAPI,
  createCourseV2CAPCE as createCourseV2CAPCEAPI,
  updateCourseV2CAPCE as updateCourseV2CAPCEAPI,
  deleteCourseV2CAPCE as deleteCourseV2CAPCEAPI,
  fetchCourseV2ANCC as fetchCourseV2ANCCAPI,
  createCourseV2ANCC as createCourseV2ANCCAPI,
  updateCourseV2ANCC as updateCourseV2ANCCAPI,
  deleteCourseV2ANCC as deleteCourseV2ANCCAPI,
  fetchCourseV2Quiz as fetchCourseV2QuizAPI,
  fetchCourseV2QuizQuestions as fetchCourseV2QuizQuestionsAPI,
  createCourseV2Quiz as createCourseV2QuizAPI,
  fetchCourseV2PublishReadiness as fetchCourseV2PublishReadinessAPI,
  updatePlanningCourseV2Publish as updatePlanningCourseV2PublishAPI,
} from './utils/course-api'

// ─── Selector state type ──────────────────────────────────────────────────────
// Extends AppRootState with the coursePipeline slice once it is registered
type CoursePipelineRootState = AppRootState & {
  coursePipeline: CoursePipelineState
}

// ─── Thunks ───────────────────────────────────────────────────────────────────

export let fetchPlanningCoursesV2 = createAsyncThunk(
  'coursePipeline/fetchPlanningCoursesV2',
  () => fetchPlanningCoursesV2API(),
)

export let fetchPlanningCourseV2Details = createAsyncThunk(
  'coursePipeline/fetchPlanningCourseV2Details',
  (courseId: string) => fetchPlanningCourseV2DetailsAPI(courseId),
)

export let fetchCourseV2Compliances = createAsyncThunk(
  'coursePipeline/fetchCourseV2Compliances',
  () =>
    fetchCourseV2CompliancesAPI().then((data) =>
      data.reduce(
        (obj, cc) => ({ ...obj, [cc.courseID]: cc }),
        {} as Record<string, CourseV2Compliance>,
      ),
    ),
)

export let fetchPlanningCoursesV2Progress = createAsyncThunk(
  'coursePipeline/fetchPlanningCoursesV2Progress',
  () =>
    fetchPlanningCoursesV2ProgressAPI().then((data) =>
      data.courses.reduce(
        (obj, cp) => ({ ...obj, [cp.courseID]: cp }),
        {} as Record<string, CourseV2Progress>,
      ),
    ),
)

export let fetchCourseV2LegacyTextContent = createAsyncThunk(
  'coursePipeline/fetchCourseV2LegacyTextContent',
  (courseId: string) => fetchCourseV2LegacyTextContentAPI(courseId),
)

export let fetchCourseV2ShowNotes = createAsyncThunk(
  'coursePipeline/fetchCourseV2ShowNotes',
  (courseId: string) => fetchCourseV2ShowNotesAPI(courseId),
)

export let deleteCourseShowNotes = createAsyncThunk(
  'coursePipeline/deleteCourseShowNotes',
  (args: { courseId: string; showNotesId: string }) =>
    deleteCourseShowNotesAPI(args.showNotesId).then(() => ({
      courseId: args.courseId,
      showNotesId: args.showNotesId,
    })),
)

export let createCourseShowNotes = createAsyncThunk(
  'coursePipeline/createCourseShowNotes',
  (args: {
    courseId: string
    payload: { name: string; description: string | null }
  }) => createCourseShowNotesAPI(args.courseId, args.payload),
)

export let updateCourseShowNotes = createAsyncThunk(
  'coursePipeline/updateCourseShowNotes',
  (args: {
    showNotesID: string
    courseID: string
    payload: { name: string; description: string | null }
  }) => updateCourseShowNotesAPI(args.showNotesID, args.payload),
)

export let fetchCourseV2Videos = createAsyncThunk(
  'coursePipeline/fetchCourseV2Videos',
  (courseId: string) => fetchCourseV2VideosAPI(courseId),
)

export let createCourseVideo = createAsyncThunk(
  'coursePipeline/createCourseVideo',
  (args: {
    courseId: string
    payload: { name: string; description: string; key: string }
  }) => createCourseVideoAPI(args.courseId, args.payload),
)

export let updateCourseVideo = createAsyncThunk(
  'coursePipeline/updateCourseVideo',
  (args: {
    videoId: string
    courseId: string
    payload: { name: string; description: string }
  }) => updateCourseVideoAPI(args.videoId, args.payload),
)

export let deleteCourseVideo = createAsyncThunk(
  'coursePipeline/deleteCourseVideo',
  (args: { videoId: string; courseId: string }) =>
    deleteCourseVideoAPI(args.videoId).then(() => args),
)

export let updateCourseV2ContentSteps = createAsyncThunk(
  'coursePipeline/updateCourseV2ContentSteps',
  (args: {
    courseId: string
    steps: {
      type: 'Video' | 'ShowNotes'
      videoID: string | null
      showNotesID: string | null
    }[]
  }) =>
    updateCourseV2ContentStepsAPI(args.courseId, args.steps).then(() => args),
)

export let createPlanningCourseV2 = createAsyncThunk(
  'coursePipeline/createPlanningCourseV2',
  (payload: {
    sectionID: string
    templateID: string
    name: string
    length: number
  }) => createPlanningCourseV2API(payload),
)

export let updatePlanningCourseV2Core = createAsyncThunk(
  'coursePipeline/updatePlanningCourseV2Core',
  (args: {
    courseId: string
    payload: {
      name: string
      sectionID: string
      templateID: string
      length: number
    }
  }) => updatePlanningCourseV2CoreAPI(args.courseId, args.payload),
)

export let updatePlanningCourseV2Syllabus = createAsyncThunk(
  'coursePipeline/updatePlanningCourseV2Syllabus',
  (args: {
    courseId: string
    payload: {
      description?: string | null
      practiceGap?: string | null
      practiceGapEvidence?: string | null
      learnerEngagement?: string | null
      learningOutcomes?: string | null
    }
  }) => updatePlanningCourseV2SyllabusAPI(args.courseId, args.payload),
)

export let updatePlanningCourseV2Objectives = createAsyncThunk(
  'coursePipeline/updatePlanningCourseV2Objectives',
  (args: { courseId: string; objectives: { text: string }[] }) =>
    updatePlanningCourseV2ObjectivesAPI(args.courseId, args.objectives),
)

export let updatePlanningCourseV2References = createAsyncThunk(
  'coursePipeline/updatePlanningCourseV2References',
  (args: { courseId: string; references: { text: string }[] }) =>
    updatePlanningCourseV2ReferencesAPI(args.courseId, args.references),
)

export let updatePlanningCourseV2Instructors = createAsyncThunk(
  'coursePipeline/updatePlanningCourseV2Instructors',
  (args: { courseId: string; instructors: { instructorID: string }[] }) =>
    updatePlanningCourseV2InstructorsAPI(args.courseId, args.instructors),
)

export let fetchPlanningCourseV2AvailableInstructors = createAsyncThunk(
  'coursePipeline/fetchPlanningCourseV2AvailableInstructors',
  () => fetchPlanningCourseV2AvailableInstructorsAPI(),
)

export let createPlanningCourseV2Instructor = createAsyncThunk(
  'coursePipeline/createPlanningCourseV2Instructor',
  (payload: { name: string; credentials?: string | null }) =>
    createPlanningCourseV2InstructorAPI(payload),
)

export let updatePlanningCourseV2Instructor = createAsyncThunk(
  'coursePipeline/updatePlanningCourseV2Instructor',
  (args: {
    instructorId: string
    payload: { name: string; credentials?: string | null }
  }) => updatePlanningCourseV2InstructorAPI(args.instructorId, args.payload),
)

export let fetchPlanningCourseV2Objectives = createAsyncThunk(
  'coursePipeline/fetchPlanningCourseV2Objectives',
  (courseId: string) => fetchPlanningCourseV2ObjectivesAPI(courseId),
)

export let fetchPlanningCourseV2References = createAsyncThunk(
  'coursePipeline/fetchPlanningCourseV2References',
  (courseId: string) => fetchPlanningCourseV2ReferencesAPI(courseId),
)

export let deletePlanningCourseV2 = createAsyncThunk(
  'coursePipeline/deletePlanningCourseV2',
  (courseId: string) => deletePlanningCourseV2API(courseId),
)

export let preArchiveCourse = createAsyncThunk(
  'coursePipeline/preArchiveCourse',
  (courseId: string) =>
    preArchiveCourseAPI(courseId).then((data) => ({ ...data, courseId })),
)

export let reactivateCourse = createAsyncThunk(
  'coursePipeline/reactivateCourse',
  (courseId: string) =>
    reactivateCourseAPI(courseId).then((data) => ({ ...data, courseId })),
)

export let archiveCourse = createAsyncThunk(
  'coursePipeline/archiveCourse',
  (courseId: string) =>
    archiveCourseAPI(courseId).then((data) => ({ ...data, courseId })),
)

export let restoreCourse = createAsyncThunk(
  'coursePipeline/restoreCourse',
  (courseId: string) =>
    restoreCourseAPI(courseId).then((data) => ({ ...data, courseId })),
)

export let fetchCourseMigratableContent = createAsyncThunk(
  'coursePipeline/fetchCourseMigratableContent',
  (courseID: string) => fetchCourseMigratableContentAPI(courseID),
)

export let migrateCourseV2Quiz = createAsyncThunk(
  'coursePipeline/migrateCourseV2Quiz',
  (courseID: string) => migrateCourseV2QuizAPI(courseID),
)

export let migrateCourseV2Accreditation = createAsyncThunk(
  'coursePipeline/migrateCourseV2Accreditation',
  (courseID: string) => migrateCourseV2AccreditationAPI(courseID),
)

export let migrateCourseV2Video = createAsyncThunk(
  'coursePipeline/migrateCourseV2Video',
  (args: { courseID: string; videoID: string }) =>
    migrateCourseV2VideoAPI(args.courseID, args.videoID).then(() => args),
)

export let migrateCourseV2ShowNote = createAsyncThunk(
  'coursePipeline/migrateCourseV2ShowNote',
  (args: { courseID: string; showNotesID: string }) =>
    migrateCourseV2ShowNoteAPI(args.courseID, args.showNotesID).then(
      () => args,
    ),
)

export let fetchPlanningSections = createAsyncThunk(
  'coursePipeline/fetchPlanningSections',
  () =>
    axios
      .get<Section[]>('/api/pipeline/planning/courses/sections')
      .then(pluckData),
)

export let fetchPlanningTemplates = createAsyncThunk(
  'coursePipeline/fetchPlanningTemplates',
  () =>
    axios
      .get<Template[]>('/api/pipeline/planning/courses/templates')
      .then(pluckData),
)

// ─── Course CAPCE thunks ──────────────────────────────────────────────────────

export let fetchCourseV2CAPCEAccreditation = createAsyncThunk(
  'coursePipeline/fetchCourseV2CAPCEAccreditation',
  (courseID: string) => fetchCourseV2CAPCEAPI(courseID),
)

export let createCourseV2CAPCEAccreditation = createAsyncThunk(
  'coursePipeline/createCourseV2CAPCEAccreditation',
  (payload: {
    courseID: string
    courseCode: string
    expirationDate: string
    applicationDate?: string | null
    submissionDate?: string | null
    topicArea: string
    classType: string
    allClearDate?: string | null
  }) =>
    createCourseV2CAPCEAPI(payload.courseID, {
      courseCode: payload.courseCode,
      expirationDate: payload.expirationDate,
      applicationDate: payload.applicationDate,
      submissionDate: payload.submissionDate,
      topicArea: payload.topicArea,
      classType: payload.classType,
      allClearDate: payload.allClearDate,
    }),
)

export let updateCourseV2CAPCEAccreditation = createAsyncThunk(
  'coursePipeline/updateCourseV2CAPCEAccreditation',
  (payload: {
    courseID: string
    id: string
    courseCode?: string | null
    expirationDate?: string | null
    applicationDate?: string | null
    submissionDate?: string | null
    topicArea?: string | null
    classType?: string | null
    allClearDate?: string | null
  }) =>
    updateCourseV2CAPCEAPI(payload.courseID, {
      id: payload.id,
      courseCode: payload.courseCode,
      expirationDate: payload.expirationDate,
      applicationDate: payload.applicationDate,
      submissionDate: payload.submissionDate,
      topicArea: payload.topicArea,
      classType: payload.classType,
      allClearDate: payload.allClearDate,
    }),
)

export let deleteCourseV2CAPCEAccreditation = createAsyncThunk(
  'coursePipeline/deleteCourseV2CAPCEAccreditation',
  (courseID: string) => deleteCourseV2CAPCEAPI(courseID),
)

// ─── Course ANCC thunks ───────────────────────────────────────────────────────

export let fetchCourseV2ANCCAccreditation = createAsyncThunk(
  'coursePipeline/fetchCourseV2ANCCAccreditation',
  (courseID: string) => fetchCourseV2ANCCAPI(courseID),
)

export let createCourseV2ANCCAccreditation = createAsyncThunk(
  'coursePipeline/createCourseV2ANCCAccreditation',
  (payload: {
    courseID: string
    courseCode: string
    expirationDate: string
    targetAudience: string[]
    educationalNeeds: string[]
    competency?: string | null
    professionalSource?: string | null
  }) =>
    createCourseV2ANCCAPI(payload.courseID, {
      courseCode: payload.courseCode,
      expirationDate: payload.expirationDate,
      targetAudience: payload.targetAudience,
      educationalNeeds: payload.educationalNeeds,
      competency: payload.competency,
      professionalSource: payload.professionalSource,
    }),
)

export let updateCourseV2ANCCAccreditation = createAsyncThunk(
  'coursePipeline/updateCourseV2ANCCAccreditation',
  (payload: {
    courseID: string
    id: string
    courseCode?: string | null
    expirationDate?: string | null
    targetAudience?: string[] | null
    educationalNeeds?: string[] | null
    competency?: string | null
    professionalSource?: string | null
  }) =>
    updateCourseV2ANCCAPI(payload.courseID, {
      id: payload.id,
      courseCode: payload.courseCode,
      expirationDate: payload.expirationDate,
      targetAudience: payload.targetAudience,
      educationalNeeds: payload.educationalNeeds,
      competency: payload.competency,
      professionalSource: payload.professionalSource,
    }),
)

export let deleteCourseV2ANCCAccreditation = createAsyncThunk(
  'coursePipeline/deleteCourseV2ANCCAccreditation',
  (courseID: string) => deleteCourseV2ANCCAPI(courseID),
)

// ─── Course Quiz thunks ───────────────────────────────────────────────────────

export let fetchCourseV2QuizThunk = createAsyncThunk(
  'coursePipeline/fetchCourseV2Quiz',
  (courseID: string) => fetchCourseV2QuizAPI(courseID),
)

export let fetchCourseV2QuizQuestionsThunk = createAsyncThunk(
  'coursePipeline/fetchCourseV2QuizQuestions',
  (courseID: string) => fetchCourseV2QuizQuestionsAPI(courseID),
)

export let createCourseV2QuizThunk = createAsyncThunk(
  'coursePipeline/createCourseV2Quiz',
  (payload: {
    courseID: string
    passingScore: number
    displayedQuestions: number
    randomizeQuestions: boolean
  }) => createCourseV2QuizAPI(payload),
)

// ─── Publish readiness + publish thunks ──────────────────────────────────────

export let fetchCourseV2PublishReadiness = createAsyncThunk(
  'coursePipeline/fetchCourseV2PublishReadiness',
  (courseID: string) => fetchCourseV2PublishReadinessAPI(courseID),
)

export let updatePlanningCourseV2Publish = createAsyncThunk(
  'coursePipeline/updatePlanningCourseV2Publish',
  (args: {
    courseID: string
    newPublicationStatus: 'published' | 'unpublished'
  }) =>
    updatePlanningCourseV2PublishAPI(
      args.courseID,
      args.newPublicationStatus,
    ).then(() => args),
)

// ─── State shape ──────────────────────────────────────────────────────────────

type CoursePipelineState = {
  planningCoursesV2: PlanningCourseV2[]
  planningCoursesV2Status: Status
  planningCourseV2Details: Record<string, PlanningCourseV2Details>
  planningCourseV2DetailsStatus: Record<string, Status>
  planningCourseV2DetailsRequestID: Record<string, string>
  courseV2LegacyTextContent: Record<string, TextContent[]>
  courseV2LegacyTextContentStatus: Record<string, Status>
  courseV2ShowNotes: Record<string, PipelineShowNotes[]>
  courseV2ShowNotesStatus: Record<string, Status>
  courseV2Videos: Record<string, PipelineVideo[]>
  courseV2VideosStatus: Record<string, Status>
  planningCourseV2ObjectivesStatus: Record<string, Status>
  planningCourseV2ReferencesStatus: Record<string, Status>
  planningCourseV2CoreSaveStatus: Record<string, Status>
  planningCourseV2SyllabusSaveStatus: Record<string, Status>
  planningCourseV2ObjectivesSaveStatus: Record<string, Status>
  planningCourseV2ReferencesSaveStatus: Record<string, Status>
  planningCourseV2InstructorsSaveStatus: Record<string, Status>
  planningCourseV2AvailableInstructors: Instructor[]
  planningCourseV2AvailableInstructorsStatus: Status
  courseV2Compliance: Record<string, CourseV2Compliance>
  courseV2ComplianceStatus: Status
  courseV2Progress: Record<string, CourseV2Progress>
  courseV2ProgressStatus: Status
  courseMigratableContent: Record<string, CourseMigratableContent>
  courseMigratableContentStatus: Record<string, Status>
  courseMigrationStatus: Status
  courseMigrationError: string | null
  deleteCourseVideoStatus: Status
  deleteCourseVideoError: string | null
  deleteCourseShowNotesStatus: Status
  deleteCourseShowNotesError: string | null
  planningSections: Section[]
  planningSectionsStatus: Status
  planningTemplates: Template[]
  planningTemplatesStatus: Status
  courseCapceAccreditations: Record<string, CAPCEAccreditationV2>
  courseCapceAccreditationsStatus: Record<string, Status>
  courseCapceCreateStatus: Record<string, Status>
  courseCapceUpdateStatus: Record<string, Status>
  courseAnccAccreditations: Record<string, ANCCAccreditation>
  courseAnccAccreditationsStatus: Record<string, Status>
  courseAnccCreateStatus: Record<string, Status>
  courseAnccUpdateStatus: Record<string, Status>
  courseQuizzes: Record<string, QuizV2>
  courseQuizzesStatus: Record<string, Status>
  courseQuizQuestions: Record<string, QuestionData[]>
  courseQuizQuestionsStatus: Record<string, Status>
  coursePublishReadiness: Record<string, CourseV2PublishReadiness>
  coursePublishReadinessStatus: Record<string, Status>
  coursePublishStatus: Record<string, Status>
  courseLifecycleOperationStatus: Status
  courseLifecycleOperationError: string | null
  courseLifecycleOperationSuccess: string | null
}

// A details response is only applied if it belongs to the most recent fetch
// and nothing invalidated the course while it was in flight — otherwise a
// stale GET could clobber values merged in by a save that landed after the
// server read.
let isLatestDetailsRequest = (
  state: CoursePipelineState,
  meta: { arg: string; requestId: string },
) =>
  state.planningCourseV2DetailsRequestID[meta.arg] === meta.requestId &&
  state.planningCourseV2DetailsStatus[meta.arg] === 'pending'

let initialState: CoursePipelineState = {
  planningCoursesV2: [],
  planningCoursesV2Status: 'uninitialized',
  planningCourseV2Details: {},
  planningCourseV2DetailsStatus: {},
  planningCourseV2DetailsRequestID: {},
  courseV2LegacyTextContent: {},
  courseV2LegacyTextContentStatus: {},
  courseV2ShowNotes: {},
  courseV2ShowNotesStatus: {},
  courseV2Videos: {},
  courseV2VideosStatus: {},
  planningCourseV2ObjectivesStatus: {},
  planningCourseV2ReferencesStatus: {},
  planningCourseV2CoreSaveStatus: {},
  planningCourseV2SyllabusSaveStatus: {},
  planningCourseV2ObjectivesSaveStatus: {},
  planningCourseV2ReferencesSaveStatus: {},
  planningCourseV2InstructorsSaveStatus: {},
  planningCourseV2AvailableInstructors: [],
  planningCourseV2AvailableInstructorsStatus: 'uninitialized',
  courseV2Compliance: {},
  courseV2ComplianceStatus: 'uninitialized',
  courseV2Progress: {},
  courseV2ProgressStatus: 'uninitialized',
  courseMigratableContent: {},
  courseMigratableContentStatus: {},
  courseMigrationStatus: 'uninitialized',
  courseMigrationError: null,
  deleteCourseVideoStatus: 'uninitialized',
  deleteCourseVideoError: null,
  deleteCourseShowNotesStatus: 'uninitialized',
  deleteCourseShowNotesError: null,
  planningSections: [],
  planningSectionsStatus: 'uninitialized',
  planningTemplates: [],
  planningTemplatesStatus: 'uninitialized',
  courseCapceAccreditations: {},
  courseCapceAccreditationsStatus: {},
  courseCapceCreateStatus: {},
  courseCapceUpdateStatus: {},
  courseAnccAccreditations: {},
  courseAnccAccreditationsStatus: {},
  courseAnccCreateStatus: {},
  courseAnccUpdateStatus: {},
  courseQuizzes: {},
  courseQuizzesStatus: {},
  courseQuizQuestions: {},
  courseQuizQuestionsStatus: {},
  coursePublishReadiness: {},
  coursePublishReadinessStatus: {},
  coursePublishStatus: {},
  courseLifecycleOperationStatus: 'uninitialized',
  courseLifecycleOperationError: null,
  courseLifecycleOperationSuccess: null,
}

// ─── Lifecycle helper ─────────────────────────────────────────────────────────

let applyLifecycleTransition = (
  state: CoursePipelineState,
  courseId: string,
  toState: LifecycleState,
) => {
  let course = state.planningCoursesV2.find((c) => c.id === courseId)
  if (course) {
    course.lifecycleState = toState
  }
}

// ─── Slice ────────────────────────────────────────────────────────────────────

let coursePipelineSlice = createSlice({
  name: 'coursePipeline',
  initialState,
  reducers: {
    clearCourseMigrationStatus: (state) => {
      state.courseMigrationStatus = 'uninitialized'
      state.courseMigrationError = null
    },
    clearCourseLifecycleOperationStatus: (state) => {
      state.courseLifecycleOperationStatus = 'uninitialized'
      state.courseLifecycleOperationError = null
      state.courseLifecycleOperationSuccess = null
    },
    clearCourseCapceCreateStatus: (state, action: { payload: string }) => {
      state.courseCapceCreateStatus[action.payload] = 'uninitialized'
    },
    clearCourseCapceUpdateStatus: (state, action: { payload: string }) => {
      state.courseCapceUpdateStatus[action.payload] = 'uninitialized'
    },
    clearCourseAnccCreateStatus: (state, action: { payload: string }) => {
      state.courseAnccCreateStatus[action.payload] = 'uninitialized'
    },
    clearCourseAnccUpdateStatus: (state, action: { payload: string }) => {
      state.courseAnccUpdateStatus[action.payload] = 'uninitialized'
    },
  },
  extraReducers: (builder) => {
    builder
      // Course v2 Planning reducers
      .addCase(fetchPlanningCoursesV2.pending, (state) => {
        state.planningCoursesV2Status = 'pending'
      })
      .addCase(fetchPlanningCoursesV2.rejected, (state) => {
        state.planningCoursesV2Status = 'rejected'
      })
      .addCase(fetchPlanningCoursesV2.fulfilled, (state, action) => {
        state.planningCoursesV2Status = 'fulfilled'
        state.planningCoursesV2 = action.payload
      })
      .addCase(createPlanningCourseV2.fulfilled, (state) => {
        state.planningCoursesV2Status = 'uninitialized'
      })
      .addCase(updatePlanningCourseV2Core.pending, (state, action) => {
        state.planningCourseV2CoreSaveStatus[action.meta.arg.courseId] =
          'pending'
      })
      .addCase(updatePlanningCourseV2Core.rejected, (state, action) => {
        state.planningCourseV2CoreSaveStatus[action.meta.arg.courseId] =
          'rejected'
      })
      .addCase(updatePlanningCourseV2Core.fulfilled, (state, action) => {
        let { courseId, payload } = action.meta.arg
        state.planningCourseV2CoreSaveStatus[courseId] = 'fulfilled'
        // Write the confirmed values now so the field doesn't flash the old
        // value; the invalidation below still refetches the full details.
        // Compliance badges are refetched by CourseField after save.
        if (state.planningCourseV2Details[courseId]) {
          state.planningCourseV2Details[courseId] = {
            ...state.planningCourseV2Details[courseId],
            ...payload,
          }
        }
        state.planningCourseV2DetailsStatus[courseId] = 'uninitialized'
        state.planningCoursesV2Status = 'uninitialized'
      })
      .addCase(updatePlanningCourseV2Syllabus.pending, (state, action) => {
        state.planningCourseV2SyllabusSaveStatus[action.meta.arg.courseId] =
          'pending'
      })
      .addCase(updatePlanningCourseV2Syllabus.rejected, (state, action) => {
        state.planningCourseV2SyllabusSaveStatus[action.meta.arg.courseId] =
          'rejected'
      })
      .addCase(updatePlanningCourseV2Syllabus.fulfilled, (state, action) => {
        let { courseId, payload } = action.meta.arg
        state.planningCourseV2SyllabusSaveStatus[courseId] = 'fulfilled'
        // The API treats null as "keep existing" (Option.orElse), so only
        // merge non-null values to match what was actually persisted.
        if (state.planningCourseV2Details[courseId]) {
          state.planningCourseV2Details[courseId] = {
            ...state.planningCourseV2Details[courseId],
            ...Object.fromEntries(
              Object.entries(payload).filter(([, v]) => v != null),
            ),
          }
        }
        state.planningCourseV2DetailsStatus[courseId] = 'uninitialized'
      })
      .addCase(updatePlanningCourseV2Objectives.pending, (state, action) => {
        state.planningCourseV2ObjectivesSaveStatus[action.meta.arg.courseId] =
          'pending'
      })
      .addCase(updatePlanningCourseV2Objectives.rejected, (state, action) => {
        state.planningCourseV2ObjectivesSaveStatus[action.meta.arg.courseId] =
          'rejected'
      })
      .addCase(updatePlanningCourseV2Objectives.fulfilled, (state, action) => {
        state.planningCourseV2ObjectivesSaveStatus[action.meta.arg.courseId] =
          'fulfilled'
        state.planningCourseV2ObjectivesStatus[action.meta.arg.courseId] =
          'fulfilled'
        if (state.planningCourseV2Details[action.meta.arg.courseId]) {
          state.planningCourseV2Details[action.meta.arg.courseId].objectives =
            action.payload
        }
      })
      .addCase(updatePlanningCourseV2References.pending, (state, action) => {
        state.planningCourseV2ReferencesSaveStatus[action.meta.arg.courseId] =
          'pending'
      })
      .addCase(updatePlanningCourseV2References.rejected, (state, action) => {
        state.planningCourseV2ReferencesSaveStatus[action.meta.arg.courseId] =
          'rejected'
      })
      .addCase(updatePlanningCourseV2References.fulfilled, (state, action) => {
        state.planningCourseV2ReferencesSaveStatus[action.meta.arg.courseId] =
          'fulfilled'
        state.planningCourseV2ReferencesStatus[action.meta.arg.courseId] =
          'fulfilled'
        if (state.planningCourseV2Details[action.meta.arg.courseId]) {
          state.planningCourseV2Details[action.meta.arg.courseId].references =
            action.payload
        }
      })
      .addCase(updatePlanningCourseV2Instructors.pending, (state, action) => {
        state.planningCourseV2InstructorsSaveStatus[action.meta.arg.courseId] =
          'pending'
      })
      .addCase(updatePlanningCourseV2Instructors.rejected, (state, action) => {
        state.planningCourseV2InstructorsSaveStatus[action.meta.arg.courseId] =
          'rejected'
      })
      .addCase(updatePlanningCourseV2Instructors.fulfilled, (state, action) => {
        state.planningCourseV2InstructorsSaveStatus[action.meta.arg.courseId] =
          'fulfilled'
        if (state.planningCourseV2Details[action.meta.arg.courseId]) {
          state.planningCourseV2Details[action.meta.arg.courseId].instructors =
            action.payload
        }
      })
      .addCase(fetchPlanningCourseV2AvailableInstructors.pending, (state) => {
        state.planningCourseV2AvailableInstructorsStatus = 'pending'
      })
      .addCase(fetchPlanningCourseV2AvailableInstructors.rejected, (state) => {
        state.planningCourseV2AvailableInstructorsStatus = 'rejected'
      })
      .addCase(
        fetchPlanningCourseV2AvailableInstructors.fulfilled,
        (state, action) => {
          state.planningCourseV2AvailableInstructorsStatus = 'fulfilled'
          let existingById = new Map(
            state.planningCourseV2AvailableInstructors.map((instructor) => [
              instructor.id,
              instructor,
            ]),
          )
          state.planningCourseV2AvailableInstructors = [
            ...action.payload,
            ...state.planningCourseV2AvailableInstructors.filter(
              (instructor) =>
                !action.payload.some((i) => i.id === instructor.id),
            ),
          ].map((instructor) => existingById.get(instructor.id) ?? instructor)
        },
      )
      .addCase(createPlanningCourseV2Instructor.pending, (state) => {
        state.planningCourseV2AvailableInstructorsStatus = 'pending'
      })
      .addCase(createPlanningCourseV2Instructor.rejected, (state) => {
        state.planningCourseV2AvailableInstructorsStatus = 'rejected'
      })
      .addCase(createPlanningCourseV2Instructor.fulfilled, (state, action) => {
        state.planningCourseV2AvailableInstructorsStatus = 'fulfilled'
        state.planningCourseV2AvailableInstructors = [
          ...state.planningCourseV2AvailableInstructors,
          action.payload,
        ]
      })
      .addCase(updatePlanningCourseV2Instructor.fulfilled, (state, action) => {
        state.planningCourseV2AvailableInstructors =
          state.planningCourseV2AvailableInstructors.map((instructor) =>
            instructor.id === action.payload.id ? action.payload : instructor,
          )
        Object.values(state.planningCourseV2Details).forEach((course) => {
          course.instructors = (course.instructors ?? []).map((instructor) =>
            instructor.id === action.payload.id ? action.payload : instructor,
          )
        })
      })
      .addCase(deletePlanningCourseV2.fulfilled, (state, action) => {
        state.planningCoursesV2 = state.planningCoursesV2.filter(
          (c) => c.id !== action.meta.arg,
        )
        state.planningCourseV2Details = Object.fromEntries(
          Object.entries(state.planningCourseV2Details).filter(
            ([id]) => id !== action.meta.arg,
          ),
        )
        state.planningCourseV2DetailsStatus = Object.fromEntries(
          Object.entries(state.planningCourseV2DetailsStatus).filter(
            ([id]) => id !== action.meta.arg,
          ),
        )
      })
      .addCase(fetchCourseMigratableContent.pending, (state, action) => {
        state.courseMigratableContentStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseMigratableContent.rejected, (state, action) => {
        state.courseMigratableContentStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseMigratableContent.fulfilled, (state, action) => {
        state.courseMigratableContentStatus[action.meta.arg] = 'fulfilled'
        state.courseMigratableContent[action.meta.arg] = action.payload
      })
      .addCase(migrateCourseV2Quiz.pending, (state) => {
        state.courseMigrationStatus = 'pending'
        state.courseMigrationError = null
      })
      .addCase(migrateCourseV2Quiz.rejected, (state) => {
        state.courseMigrationStatus = 'rejected'
        state.courseMigrationError = 'Migration failed. Please try again.'
      })
      .addCase(migrateCourseV2Quiz.fulfilled, (state) => {
        state.courseMigrationStatus = 'fulfilled'
        state.courseMigrationError = null
      })
      .addCase(migrateCourseV2Accreditation.pending, (state) => {
        state.courseMigrationStatus = 'pending'
        state.courseMigrationError = null
      })
      .addCase(migrateCourseV2Accreditation.rejected, (state) => {
        state.courseMigrationStatus = 'rejected'
        state.courseMigrationError = 'Migration failed. Please try again.'
      })
      .addCase(migrateCourseV2Accreditation.fulfilled, (state) => {
        state.courseMigrationStatus = 'fulfilled'
        state.courseMigrationError = null
      })
      .addCase(migrateCourseV2Video.fulfilled, (state, action) => {
        let { courseID, videoID } = action.payload
        let content = state.courseMigratableContent[courseID]
        if (content) {
          content.videos = content.videos.map((v) =>
            v.id === videoID ? { ...v, isMigrated: true } : v,
          )
        }
        state.courseV2VideosStatus[courseID] = 'uninitialized'
      })
      .addCase(migrateCourseV2ShowNote.fulfilled, (state, action) => {
        let { courseID, showNotesID } = action.payload
        let content = state.courseMigratableContent[courseID]
        if (content) {
          content.showNotes = content.showNotes.map((sn) =>
            sn.id === showNotesID ? { ...sn, isMigrated: true } : sn,
          )
        }
        state.courseV2ShowNotesStatus[courseID] = 'uninitialized'
      })
      .addCase(fetchPlanningCourseV2Details.pending, (state, action) => {
        state.planningCourseV2DetailsStatus[action.meta.arg] = 'pending'
        state.planningCourseV2DetailsRequestID[action.meta.arg] =
          action.meta.requestId
      })
      .addCase(fetchPlanningCourseV2Details.rejected, (state, action) => {
        if (!isLatestDetailsRequest(state, action.meta)) return
        state.planningCourseV2DetailsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchPlanningCourseV2Details.fulfilled, (state, action) => {
        if (!isLatestDetailsRequest(state, action.meta)) return
        state.planningCourseV2DetailsStatus[action.meta.arg] = 'fulfilled'
        state.planningCourseV2Details[action.meta.arg] = action.payload
      })
      .addCase(fetchPlanningCourseV2Objectives.pending, (state, action) => {
        state.planningCourseV2ObjectivesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchPlanningCourseV2Objectives.rejected, (state, action) => {
        state.planningCourseV2ObjectivesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchPlanningCourseV2Objectives.fulfilled, (state, action) => {
        state.planningCourseV2ObjectivesStatus[action.meta.arg] = 'fulfilled'
        if (state.planningCourseV2Details[action.meta.arg]) {
          state.planningCourseV2Details[action.meta.arg].objectives =
            action.payload
        }
      })
      .addCase(fetchPlanningCourseV2References.pending, (state, action) => {
        state.planningCourseV2ReferencesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchPlanningCourseV2References.rejected, (state, action) => {
        state.planningCourseV2ReferencesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchPlanningCourseV2References.fulfilled, (state, action) => {
        state.planningCourseV2ReferencesStatus[action.meta.arg] = 'fulfilled'
        if (state.planningCourseV2Details[action.meta.arg]) {
          state.planningCourseV2Details[action.meta.arg].references =
            action.payload
        }
      })
      .addCase(fetchCourseV2LegacyTextContent.pending, (state, action) => {
        state.courseV2LegacyTextContentStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseV2LegacyTextContent.rejected, (state, action) => {
        state.courseV2LegacyTextContentStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseV2LegacyTextContent.fulfilled, (state, action) => {
        state.courseV2LegacyTextContentStatus[action.meta.arg] = 'fulfilled'
        state.courseV2LegacyTextContent[action.meta.arg] = action.payload
      })
      .addCase(fetchCourseV2ShowNotes.pending, (state, action) => {
        state.courseV2ShowNotesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseV2ShowNotes.rejected, (state, action) => {
        state.courseV2ShowNotesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseV2ShowNotes.fulfilled, (state, action) => {
        state.courseV2ShowNotesStatus[action.meta.arg] = 'fulfilled'
        state.courseV2ShowNotes[action.meta.arg] = action.payload
      })
      .addCase(createCourseShowNotes.fulfilled, (state, action) => {
        let { courseId } = action.meta.arg
        state.courseV2ShowNotes[courseId] = [
          ...(state.courseV2ShowNotes[courseId] ?? []),
          action.payload,
        ]
        // Reset legacy text content status so it refetches on next view
        state.courseV2LegacyTextContentStatus[courseId] = 'uninitialized'
      })
      .addCase(deleteCourseShowNotes.pending, (state) => {
        state.deleteCourseShowNotesStatus = 'pending'
        state.deleteCourseShowNotesError = null
      })
      .addCase(deleteCourseShowNotes.rejected, (state) => {
        state.deleteCourseShowNotesStatus = 'rejected'
        state.deleteCourseShowNotesError =
          'Failed to delete show notes. Please try again.'
      })
      .addCase(deleteCourseShowNotes.fulfilled, (state, action) => {
        state.deleteCourseShowNotesStatus = 'fulfilled'
        state.deleteCourseShowNotesError = null
        let { courseId } = action.payload
        let { showNotesId } = action.payload
        state.courseV2ShowNotes[courseId] = (
          state.courseV2ShowNotes[courseId] ?? []
        ).filter((sn) => sn.id !== showNotesId)
      })
      .addCase(updateCourseShowNotes.fulfilled, (state, action) => {
        let { courseID } = action.meta.arg
        state.courseV2ShowNotes[courseID] = (
          state.courseV2ShowNotes[courseID] ?? []
        ).map((sn) => (sn.id === action.payload.id ? action.payload : sn))
      })
      .addCase(fetchCourseV2Videos.pending, (state, action) => {
        state.courseV2VideosStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseV2Videos.rejected, (state, action) => {
        state.courseV2VideosStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseV2Videos.fulfilled, (state, action) => {
        state.courseV2VideosStatus[action.meta.arg] = 'fulfilled'
        state.courseV2Videos[action.meta.arg] = action.payload
      })
      .addCase(createCourseVideo.fulfilled, (state, action) => {
        let { courseId } = action.meta.arg
        state.courseV2Videos[courseId] = [
          ...(state.courseV2Videos[courseId] ?? []),
          action.payload,
        ]
      })
      .addCase(updateCourseVideo.fulfilled, (state, action) => {
        let { courseId } = action.meta.arg
        state.courseV2Videos[courseId] = (
          state.courseV2Videos[courseId] ?? []
        ).map((v) => (v.id === action.payload.id ? action.payload : v))
      })
      .addCase(deleteCourseVideo.pending, (state) => {
        state.deleteCourseVideoStatus = 'pending'
        state.deleteCourseVideoError = null
      })
      .addCase(deleteCourseVideo.rejected, (state) => {
        state.deleteCourseVideoStatus = 'rejected'
        state.deleteCourseVideoError =
          'Failed to delete video. Please try again.'
      })
      .addCase(deleteCourseVideo.fulfilled, (state, action) => {
        state.deleteCourseVideoStatus = 'fulfilled'
        state.deleteCourseVideoError = null
        let { videoId, courseId } = action.payload
        state.courseV2Videos[courseId] = (
          state.courseV2Videos[courseId] ?? []
        ).filter((v) => v.id !== videoId)
      })
      .addCase(updateCourseV2ContentSteps.fulfilled, (state, action) => {
        state.planningCourseV2DetailsStatus[action.meta.arg.courseId] =
          'uninitialized'
      })
      .addCase(fetchCourseV2Compliances.pending, (state) => {
        state.courseV2ComplianceStatus = 'pending'
      })
      .addCase(fetchCourseV2Compliances.rejected, (state) => {
        state.courseV2ComplianceStatus = 'rejected'
      })
      .addCase(fetchCourseV2Compliances.fulfilled, (state, action) => {
        state.courseV2ComplianceStatus = 'fulfilled'
        state.courseV2Compliance = action.payload
      })
      .addCase(fetchPlanningCoursesV2Progress.pending, (state) => {
        state.courseV2ProgressStatus = 'pending'
      })
      .addCase(fetchPlanningCoursesV2Progress.rejected, (state) => {
        state.courseV2ProgressStatus = 'rejected'
      })
      .addCase(fetchPlanningCoursesV2Progress.fulfilled, (state, action) => {
        state.courseV2ProgressStatus = 'fulfilled'
        state.courseV2Progress = action.payload
      })
      .addCase(fetchPlanningSections.pending, (state) => {
        state.planningSectionsStatus = 'pending'
      })
      .addCase(fetchPlanningSections.rejected, (state) => {
        state.planningSectionsStatus = 'rejected'
      })
      .addCase(fetchPlanningSections.fulfilled, (state, action) => {
        state.planningSectionsStatus = 'fulfilled'
        state.planningSections = action.payload
      })
      .addCase(fetchPlanningTemplates.pending, (state) => {
        state.planningTemplatesStatus = 'pending'
      })
      .addCase(fetchPlanningTemplates.rejected, (state) => {
        state.planningTemplatesStatus = 'rejected'
      })
      .addCase(fetchPlanningTemplates.fulfilled, (state, action) => {
        state.planningTemplatesStatus = 'fulfilled'
        state.planningTemplates = action.payload
      })
      // Course CAPCE reducers
      .addCase(fetchCourseV2CAPCEAccreditation.pending, (state, action) => {
        state.courseCapceAccreditationsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseV2CAPCEAccreditation.rejected, (state, action) => {
        state.courseCapceAccreditationsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseV2CAPCEAccreditation.fulfilled, (state, action) => {
        state.courseCapceAccreditationsStatus[action.meta.arg] = 'fulfilled'
        state.courseCapceAccreditations[action.meta.arg] = action.payload
      })
      .addCase(createCourseV2CAPCEAccreditation.pending, (state, action) => {
        state.courseCapceCreateStatus[action.meta.arg.courseID] = 'pending'
      })
      .addCase(createCourseV2CAPCEAccreditation.rejected, (state, action) => {
        state.courseCapceCreateStatus[action.meta.arg.courseID] = 'rejected'
      })
      .addCase(createCourseV2CAPCEAccreditation.fulfilled, (state, action) => {
        state.courseCapceCreateStatus[action.meta.arg.courseID] = 'fulfilled'
      })
      .addCase(updateCourseV2CAPCEAccreditation.pending, (state, action) => {
        state.courseCapceUpdateStatus[action.meta.arg.courseID] = 'pending'
      })
      .addCase(updateCourseV2CAPCEAccreditation.rejected, (state, action) => {
        state.courseCapceUpdateStatus[action.meta.arg.courseID] = 'rejected'
      })
      .addCase(updateCourseV2CAPCEAccreditation.fulfilled, (state, action) => {
        state.courseCapceUpdateStatus[action.meta.arg.courseID] = 'fulfilled'
      })
      // Course ANCC reducers
      .addCase(fetchCourseV2ANCCAccreditation.pending, (state, action) => {
        state.courseAnccAccreditationsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseV2ANCCAccreditation.rejected, (state, action) => {
        state.courseAnccAccreditationsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseV2ANCCAccreditation.fulfilled, (state, action) => {
        state.courseAnccAccreditationsStatus[action.meta.arg] = 'fulfilled'
        state.courseAnccAccreditations[action.meta.arg] = action.payload
      })
      .addCase(createCourseV2ANCCAccreditation.pending, (state, action) => {
        state.courseAnccCreateStatus[action.meta.arg.courseID] = 'pending'
      })
      .addCase(createCourseV2ANCCAccreditation.rejected, (state, action) => {
        state.courseAnccCreateStatus[action.meta.arg.courseID] = 'rejected'
      })
      .addCase(createCourseV2ANCCAccreditation.fulfilled, (state, action) => {
        state.courseAnccCreateStatus[action.meta.arg.courseID] = 'fulfilled'
      })
      .addCase(updateCourseV2ANCCAccreditation.pending, (state, action) => {
        state.courseAnccUpdateStatus[action.meta.arg.courseID] = 'pending'
      })
      .addCase(updateCourseV2ANCCAccreditation.rejected, (state, action) => {
        state.courseAnccUpdateStatus[action.meta.arg.courseID] = 'rejected'
      })
      .addCase(updateCourseV2ANCCAccreditation.fulfilled, (state, action) => {
        state.courseAnccUpdateStatus[action.meta.arg.courseID] = 'fulfilled'
      })
      // Course Quiz reducers
      .addCase(fetchCourseV2QuizThunk.pending, (state, action) => {
        state.courseQuizzesStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseV2QuizThunk.rejected, (state, action) => {
        state.courseQuizzesStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseV2QuizThunk.fulfilled, (state, action) => {
        state.courseQuizzesStatus[action.meta.arg] = 'fulfilled'
        state.courseQuizzes[action.meta.arg] = action.payload
      })
      .addCase(createCourseV2QuizThunk.pending, (state, action) => {
        state.courseQuizzesStatus[action.meta.arg.courseID] = 'pending'
      })
      .addCase(createCourseV2QuizThunk.rejected, (state, action) => {
        state.courseQuizzesStatus[action.meta.arg.courseID] = 'rejected'
      })
      .addCase(createCourseV2QuizThunk.fulfilled, (state, action) => {
        state.courseQuizzesStatus[action.meta.arg.courseID] = 'fulfilled'
        state.courseQuizzes[action.meta.arg.courseID] = action.payload
      })
      .addCase(fetchCourseV2QuizQuestionsThunk.pending, (state, action) => {
        state.courseQuizQuestionsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseV2QuizQuestionsThunk.rejected, (state, action) => {
        state.courseQuizQuestionsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseV2QuizQuestionsThunk.fulfilled, (state, action) => {
        state.courseQuizQuestionsStatus[action.meta.arg] = 'fulfilled'
        state.courseQuizQuestions[action.meta.arg] = action.payload
      })
      // Publish readiness reducers
      .addCase(fetchCourseV2PublishReadiness.pending, (state, action) => {
        state.coursePublishReadinessStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchCourseV2PublishReadiness.rejected, (state, action) => {
        state.coursePublishReadinessStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchCourseV2PublishReadiness.fulfilled, (state, action) => {
        state.coursePublishReadinessStatus[action.meta.arg] = 'fulfilled'
        state.coursePublishReadiness[action.meta.arg] = action.payload
      })
      // Publish reducers
      .addCase(updatePlanningCourseV2Publish.pending, (state, action) => {
        state.coursePublishStatus[action.meta.arg.courseID] = 'pending'
      })
      .addCase(updatePlanningCourseV2Publish.rejected, (state, action) => {
        state.coursePublishStatus[action.meta.arg.courseID] = 'rejected'
      })
      .addCase(updatePlanningCourseV2Publish.fulfilled, (state, action) => {
        state.coursePublishStatus[action.meta.arg.courseID] = 'fulfilled'
        // Invalidate so the header chip re-fetches the updated published status
        state.planningCourseV2DetailsStatus[action.meta.arg.courseID] =
          'uninitialized'
        state.planningCoursesV2Status = 'uninitialized'
        // Invalidate readiness so it re-fetches after publish/unpublish
        state.coursePublishReadinessStatus[action.meta.arg.courseID] =
          'uninitialized'
      })
      // Course Lifecycle reducers
      .addCase(preArchiveCourse.pending, (state) => {
        state.courseLifecycleOperationStatus = 'pending'
        state.courseLifecycleOperationError = null
        state.courseLifecycleOperationSuccess = null
      })
      .addCase(preArchiveCourse.fulfilled, (state, action) => {
        state.courseLifecycleOperationStatus = 'fulfilled'
        state.courseLifecycleOperationSuccess = 'Course moved to pre-archive'
        applyLifecycleTransition(
          state,
          action.payload.courseId,
          action.payload.toState,
        )
      })
      .addCase(preArchiveCourse.rejected, (state, action) => {
        state.courseLifecycleOperationStatus = 'rejected'
        state.courseLifecycleOperationError =
          action.error.message || 'Failed to move course to pre-archive'
      })
      .addCase(reactivateCourse.pending, (state) => {
        state.courseLifecycleOperationStatus = 'pending'
        state.courseLifecycleOperationError = null
        state.courseLifecycleOperationSuccess = null
      })
      .addCase(reactivateCourse.fulfilled, (state, action) => {
        state.courseLifecycleOperationStatus = 'fulfilled'
        state.courseLifecycleOperationSuccess = 'Course reactivated'
        applyLifecycleTransition(
          state,
          action.payload.courseId,
          action.payload.toState,
        )
      })
      .addCase(reactivateCourse.rejected, (state, action) => {
        state.courseLifecycleOperationStatus = 'rejected'
        state.courseLifecycleOperationError =
          action.error.message || 'Failed to reactivate course'
      })
      .addCase(archiveCourse.pending, (state) => {
        state.courseLifecycleOperationStatus = 'pending'
        state.courseLifecycleOperationError = null
        state.courseLifecycleOperationSuccess = null
      })
      .addCase(archiveCourse.fulfilled, (state, action) => {
        state.courseLifecycleOperationStatus = 'fulfilled'
        state.courseLifecycleOperationSuccess = 'Course archived'
        applyLifecycleTransition(
          state,
          action.payload.courseId,
          action.payload.toState,
        )
      })
      .addCase(archiveCourse.rejected, (state, action) => {
        state.courseLifecycleOperationStatus = 'rejected'
        state.courseLifecycleOperationError =
          action.error.message || 'Failed to archive course'
      })
      .addCase(restoreCourse.pending, (state) => {
        state.courseLifecycleOperationStatus = 'pending'
        state.courseLifecycleOperationError = null
        state.courseLifecycleOperationSuccess = null
      })
      .addCase(restoreCourse.fulfilled, (state, action) => {
        state.courseLifecycleOperationStatus = 'fulfilled'
        state.courseLifecycleOperationSuccess = 'Course restored to pre-archive'
        applyLifecycleTransition(
          state,
          action.payload.courseId,
          action.payload.toState,
        )
      })
      .addCase(restoreCourse.rejected, (state, action) => {
        state.courseLifecycleOperationStatus = 'rejected'
        state.courseLifecycleOperationError =
          action.error.message || 'Failed to restore course'
      })
  },
})

// ─── Actions ──────────────────────────────────────────────────────────────────

export let {
  clearCourseMigrationStatus,
  clearCourseCapceCreateStatus,
  clearCourseCapceUpdateStatus,
  clearCourseAnccCreateStatus,
  clearCourseAnccUpdateStatus,
  clearCourseLifecycleOperationStatus,
} = coursePipelineSlice.actions

// ─── Selectors ────────────────────────────────────────────────────────────────

export let selectPlanningCoursesV2 = (state: CoursePipelineRootState) =>
  state.coursePipeline.planningCoursesV2
export let selectPlanningCoursesV2Status = (state: CoursePipelineRootState) =>
  state.coursePipeline.planningCoursesV2Status
export let selectPlanningCourseV2Details = (state: CoursePipelineRootState) =>
  state.coursePipeline.planningCourseV2Details
export let selectPlanningCourseV2DetailsStatus = (
  state: CoursePipelineRootState,
) => state.coursePipeline.planningCourseV2DetailsStatus
export let selectPlanningCourseV2DetailsByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2Details[courseID]
export let selectPlanningCourseV2DetailsStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2DetailsStatus[courseID]
export let selectPlanningCourseV2CoreSaveStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2CoreSaveStatus[courseID]
export let selectPlanningCourseV2SyllabusSaveStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2SyllabusSaveStatus[courseID]
export let selectPlanningCourseV2ObjectivesSaveStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2ObjectivesSaveStatus[courseID]
export let selectPlanningCourseV2ReferencesSaveStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2ReferencesSaveStatus[courseID]
export let selectPlanningCourseV2InstructorsSaveStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2InstructorsSaveStatus[courseID]
export let selectPlanningCourseV2AvailableInstructors = (
  state: CoursePipelineRootState,
) => state.coursePipeline.planningCourseV2AvailableInstructors
export let selectPlanningCourseV2AvailableInstructorsStatus = (
  state: CoursePipelineRootState,
) => state.coursePipeline.planningCourseV2AvailableInstructorsStatus
export let selectPlanningCourseV2ObjectivesStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2ObjectivesStatus[courseID]
export let selectPlanningCourseV2ReferencesStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.planningCourseV2ReferencesStatus[courseID]
export let selectCourseV2LegacyTextContentByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseV2LegacyTextContent[courseID] ?? []
export let selectCourseV2LegacyTextContentStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseV2LegacyTextContentStatus[courseID]
export let selectCourseV2ShowNotesByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseV2ShowNotes[courseID] ?? []
export let selectCourseV2ShowNotesStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseV2ShowNotesStatus[courseID]
export let selectCourseV2VideosByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseV2Videos[courseID] ?? []
export let selectCourseV2VideosStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseV2VideosStatus[courseID]

export let selectDeleteCourseVideoStatus = (state: CoursePipelineRootState) =>
  state.coursePipeline.deleteCourseVideoStatus
export let selectDeleteCourseVideoError = (state: CoursePipelineRootState) =>
  state.coursePipeline.deleteCourseVideoError

export let selectDeleteCourseShowNotesStatus = (
  state: CoursePipelineRootState,
) => state.coursePipeline.deleteCourseShowNotesStatus
export let selectDeleteCourseShowNotesError = (
  state: CoursePipelineRootState,
) => state.coursePipeline.deleteCourseShowNotesError

export let selectCourseV2Compliance = (state: CoursePipelineRootState) =>
  state.coursePipeline.courseV2Compliance
export let selectCourseV2ComplianceStatus = (state: CoursePipelineRootState) =>
  state.coursePipeline.courseV2ComplianceStatus
export let selectCourseV2ComplianceByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseV2Compliance[courseID]
export let selectCourseV2Progress = (state: CoursePipelineRootState) =>
  state.coursePipeline.courseV2Progress
export let selectCourseV2ProgressStatus = (state: CoursePipelineRootState) =>
  state.coursePipeline.courseV2ProgressStatus
export let selectCourseV2ProgressByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseV2Progress[courseID]

export let selectCourseMigratableContentByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseMigratableContent[courseID]
export let selectCourseMigratableContentStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseMigratableContentStatus[courseID]
export let selectCourseMigrationStatus = (state: CoursePipelineRootState) =>
  state.coursePipeline.courseMigrationStatus
export let selectCourseMigrationError = (state: CoursePipelineRootState) =>
  state.coursePipeline.courseMigrationError

export let selectPlanningSections = (state: CoursePipelineRootState) =>
  state.coursePipeline.planningSections
export let selectPlanningSectionsStatus = (state: CoursePipelineRootState) =>
  state.coursePipeline.planningSectionsStatus

export let selectPlanningTemplates = (state: CoursePipelineRootState) =>
  state.coursePipeline.planningTemplates
export let selectPlanningTemplatesStatus = (state: CoursePipelineRootState) =>
  state.coursePipeline.planningTemplatesStatus

export let selectCourseV2CAPCEAccreditationByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseCapceAccreditations[courseID]
export let selectCourseV2CAPCEAccreditationStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseCapceAccreditationsStatus[courseID]
export let selectCourseV2CAPCECreateStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseCapceCreateStatus[courseID]
export let selectCourseV2CAPCEUpdateStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseCapceUpdateStatus[courseID]

export let selectCourseV2ANCCAccreditationByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseAnccAccreditations[courseID]
export let selectCourseV2ANCCAccreditationStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseAnccAccreditationsStatus[courseID]
export let selectCourseV2ANCCCreateStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseAnccCreateStatus[courseID]
export let selectCourseV2ANCCUpdateStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseAnccUpdateStatus[courseID]

export let selectCourseV2QuizByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseQuizzes[courseID]
export let selectCourseV2QuizStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseQuizzesStatus[courseID]

export let selectCourseV2QuizQuestionsByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseQuizQuestions[courseID]
export let selectCourseV2QuizQuestionsStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.courseQuizQuestionsStatus[courseID]

export let selectCourseV2PublishReadinessByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.coursePublishReadiness[courseID]
export let selectCourseV2PublishReadinessStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.coursePublishReadinessStatus[courseID]
export let selectCourseV2PublishStatusByCourseID =
  (courseID: string) => (state: CoursePipelineRootState) =>
    state.coursePipeline.coursePublishStatus[courseID]

export let selectCourseLifecycleOperationStatus = (
  state: CoursePipelineRootState,
) => state.coursePipeline.courseLifecycleOperationStatus
export let selectCourseLifecycleOperationError = (
  state: CoursePipelineRootState,
) => state.coursePipeline.courseLifecycleOperationError
export let selectCourseLifecycleOperationSuccess = (
  state: CoursePipelineRootState,
) => state.coursePipeline.courseLifecycleOperationSuccess

export default coursePipelineSlice.reducer
